import {
	type IdentityHomeAnswer,
	IdentityHomeAnswerSchema,
	SignInAddressAnswerSchema,
} from "@elaso/shared/schemas/identity-home";
import {
	type LaunchTarget,
	LTI_EXCHANGE_ERROR_CODES,
	type LtiExchangeErrorCode,
	type LtiExchangeResponse,
} from "@elaso/shared/types/lti";
import {
	isSessionTokenRefusalCode,
	type SessionTokenRefusalCode,
} from "@elaso/shared/types/session-token";
import { z } from "zod";
import { getConnectionTarget } from "./connection-target";

type TokenResponse = {
	token: string;
};

type ErrorResponse = {
	error: string;
	code?: unknown;
};

/**
 * The server REFUSED to open a session for this identity
 * (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md, *The session is
 * bound*, TECH-3354): the token named no GCIP tenant, or named one this
 * Instance does not sign anybody in at, or the account has no User inside it.
 *
 * Distinct from the plain `Error` every other failure of this call throws, and
 * carried as a code rather than read out of the sentence, because the auth store
 * ACTS on it: a refused session is signed out, where a network blip or an
 * expired token is retried with a fresh one. Whatever the server said is kept
 * as the message, for the log and for whoever reads it.
 */
export class SessionRefusedError extends Error {
	constructor(
		readonly code: SessionTokenRefusalCode,
		message: string
	) {
		super(message);
		this.name = "SessionRefusedError";
	}
}

/** Request a one-time WebSocket authentication token from the server. */
export async function requestAuthToken(
	firebaseIdToken: string,
	serverUrl = getConnectionTarget().serverUrl
): Promise<string> {
	const response = await fetch(`${serverUrl}/auth/token`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ idToken: firebaseIdToken }),
	});

	if (!response.ok) {
		const error: ErrorResponse = await response.json();
		const message = error.error || "Failed to get auth token";
		if (isSessionTokenRefusalCode(error.code)) {
			throw new SessionRefusedError(error.code, message);
		}
		throw new Error(message);
	}

	const data: TokenResponse = await response.json();
	return data.token;
}

/**
 * A failed `/lti/exchange`, carrying the server's code rather than a message.
 *
 * Deliberately unlike the two calls in this file, which throw prose. Nothing the
 * exchange can refuse has a sentence that helps whoever reads it, and the copy
 * for all of them lives in one place, `lib/lti/error-copy.ts`, shared with the
 * failures the launch itself produced.
 */
export class LtiExchangeError extends Error {
	readonly code: LtiExchangeErrorCode;

	constructor(code: LtiExchangeErrorCode) {
		super(`LTI exchange refused: ${code}`);
		this.name = "LtiExchangeError";
		this.code = code;
	}
}

const isExchangeErrorCode = (value: unknown): value is LtiExchangeErrorCode =>
	typeof value === "string" &&
	(LTI_EXCHANGE_ERROR_CODES as readonly string[]).includes(value);

/** Whatever the server said, as long as it is a code we know. */
const readExchangeError = async (
	response: Response
): Promise<LtiExchangeErrorCode> => {
	try {
		const body: unknown = await response.json();
		const code = (body as { code?: unknown } | null)?.code;
		if (isExchangeErrorCode(code)) {
			return code;
		}
	} catch {
		// A non-JSON body from a proxy rather than from us.
	}
	return "internal";
};

/**
 * Trade the one-time handoff code an LTI launch put in the URL fragment for a
 * Firebase custom token (TECH-3184's `POST /lti/exchange`).
 *
 * Single-use and short-lived on the server side, so this must be called exactly
 * once per code. `lib/lti/launch-flow.ts` owns that guarantee, not this function.
 */
export async function exchangeLtiCode(
	code: string
): Promise<LtiExchangeResponse> {
	let response: Response;
	try {
		response = await fetch(`${getConnectionTarget().serverUrl}/lti/exchange`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ code }),
		});
	} catch {
		// Offline, DNS and CORS are indistinguishable from here, and all of them
		// mean the same thing to the launcher, that the tool could not be reached.
		throw new LtiExchangeError("exchange_unavailable");
	}

	if (!response.ok) {
		throw new LtiExchangeError(await readExchangeError(response));
	}

	let data: unknown;
	try {
		data = await response.json();
	} catch {
		// A 200 with a body that is not JSON at all, so a proxy's rather than ours.
		// Same meaning as a 200 that misses the contract, and it must arrive as an
		// `LtiExchangeError` either way. A raw `SyntaxError` would reach
		// `completeLtiLaunch` as an unexpected post-exchange failure and be filed to
		// Sentry as a launch bug.
		throw new LtiExchangeError("internal");
	}
	const { customToken, target, gcipTenantId } = (data ?? {}) as Partial<
		Record<keyof LtiExchangeResponse, unknown>
	>;
	const launchTarget = parseLaunchTarget(target);
	if (
		typeof customToken !== "string" ||
		launchTarget === null ||
		// The pool the token was minted in (TECH-3351). Required, not optional: a
		// body without it comes from a server that minted untenanted, and signing
		// in with the client still pointed at the project-wide pool is exactly the
		// "any account, any address" record 33 ends. Refusing shows the launcher an
		// error page instead of a session in the wrong pool.
		typeof gcipTenantId !== "string"
	) {
		// A 200 that is not the contract. Refusing beats handing a half-typed
		// object to `signInWithCustomToken`.
		throw new LtiExchangeError("internal");
	}
	return { customToken, target: launchTarget, gcipTenantId };
}

const parseLaunchTarget = (value: unknown): LaunchTarget | null => {
	if (typeof value !== "object" || value === null) return null;
	const { kind, workspaceId, connectionId } = value as Partial<
		Record<"kind" | "workspaceId" | "connectionId", unknown>
	>;
	if (kind === "workspace" && typeof workspaceId === "string") {
		return { kind, workspaceId };
	}
	if (kind === "stub" && typeof connectionId === "string") {
		return { kind, connectionId };
	}
	return null;
};

const HTTP_NOT_FOUND = 404;

/**
 * How long the resolver waits for an answer. `fetch` has none of its own, and
 * the sign-in page renders nothing but a spinner until this settles, so a
 * connection that stalls rather than fails would hold a visitor there for as
 * long as they were willing to wait. Ten seconds turns that into the
 * `unavailable` line the page already has for it.
 */
const RESOLVE_TIMEOUT_MS = 10_000;

/**
 * What `resolveIdentityHome` found, in the three shapes the sign-in page shows
 * (TECH-3350).
 *
 * `not_found` and `unavailable` are kept apart because the page says two
 * different things, and telling a server outage from an unknown address leaks
 * nothing: the server has already made every REASON an address resolves to
 * nothing into one 404.
 */
export type IdentityHomeResolution =
	| { ok: true; home: IdentityHomeAnswer }
	| { ok: false; reason: "not_found" | "unavailable" };

/**
 * Ask the server which identity home this page's address belongs to, before
 * anything is rendered and long before a credential is entered
 * (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md).
 *
 * The WHOLE address goes, exactly as the browser holds it. Where the label lives
 * in it is the server's business, so this client keeps working when TECH-3364
 * moves the label in front of the host.
 *
 * Everything that is not a 404 and not an answer matching the contract is
 * `unavailable`: offline, DNS, CORS, a 500, a proxy's HTML error page, a 200
 * carrying something else, a connection that stalls past the timeout. All of
 * them mean the same thing to a visitor, that we could not ask, and none of them
 * may be allowed to read as "your Organisation does not exist".
 */
export async function resolveIdentityHome(
	address: string
): Promise<IdentityHomeResolution> {
	let response: Response;
	try {
		response = await fetch(
			`${getConnectionTarget().serverUrl}/auth/identity-home?address=${encodeURIComponent(address)}`,
			{ signal: AbortSignal.timeout(RESOLVE_TIMEOUT_MS) }
		);
	} catch {
		return { ok: false, reason: "unavailable" };
	}

	if (response.status === HTTP_NOT_FOUND) {
		return { ok: false, reason: "not_found" };
	}
	if (!response.ok) {
		return { ok: false, reason: "unavailable" };
	}

	let data: unknown;
	try {
		data = await response.json();
	} catch {
		return { ok: false, reason: "unavailable" };
	}
	const parsed = IdentityHomeAnswerSchema.safeParse(data);
	if (!parsed.success) {
		// A 200 that is not the contract. Rendering a form from it would set a
		// tenant nobody can sign in at, which fails as a wrong password does and
		// would be unexplainable to whoever hit it.
		return { ok: false, reason: "unavailable" };
	}
	return { ok: true, home: parsed.data };
}

/**
 * What `findSignInAddress` found: the address to go to, or which of the two
 * lines the find-your-Organisation page shows, kept apart for the same reason
 * {@link IdentityHomeResolution} keeps them apart.
 */
export type SignInAddressResolution =
	| { ok: true; address: string }
	| { ok: false; reason: "not_found" | "unavailable" };

/**
 * Asks the server for a sign-in address. Everything that is not a 404 and not
 * an answer matching the contract is `unavailable`, as for
 * {@link resolveIdentityHome}.
 */
async function askForSignInAddress(
	path: string
): Promise<SignInAddressResolution> {
	let response: Response;
	try {
		response = await fetch(`${getConnectionTarget().serverUrl}${path}`, {
			signal: AbortSignal.timeout(RESOLVE_TIMEOUT_MS),
		});
	} catch {
		return { ok: false, reason: "unavailable" };
	}

	if (response.status === HTTP_NOT_FOUND) {
		return { ok: false, reason: "not_found" };
	}
	if (!response.ok) {
		return { ok: false, reason: "unavailable" };
	}

	let data: unknown;
	try {
		data = await response.json();
	} catch {
		return { ok: false, reason: "unavailable" };
	}
	const parsed = SignInAddressAnswerSchema.safeParse(data);
	if (!parsed.success) {
		return { ok: false, reason: "unavailable" };
	}
	return { ok: true, address: parsed.data.address };
}

/**
 * Ask the server where the label somebody typed signs in (TECH-3353). The label
 * goes as typed and the address comes back whole, so this client never builds
 * one and keeps working when TECH-3364 moves the label in front of the host.
 */
export const findSignInAddress = (
	label: string
): Promise<SignInAddressResolution> =>
	askForSignInAddress(
		`/auth/sign-in-address?label=${encodeURIComponent(label)}`
	);

export const findInstanceSignInAddress = (): Promise<SignInAddressResolution> =>
	askForSignInAddress("/auth/instance-sign-in-address");

/**
 * "Forgot password?" (TECH-3464). `address` is the sign-in page's own, which
 * names the identity home whose pool the account is looked for in. The server
 * answers the same whoever asks, so there is nothing to read back.
 */
export async function requestPasswordReset(
	email: string,
	address: string
): Promise<void> {
	const response = await fetch(
		`${getConnectionTarget().serverUrl}/auth/forgot-password`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ email, address }),
		}
	);

	// Swallow server-side errors, or the endpoint becomes an email-enumeration oracle.
	await response.text();
}

/**
 * The set-password page's three calls (TECH-3456). The token is a credential, so
 * it goes in the request body and never in a URL, where web servers and proxies
 * would log it. `address` is the page's own address, which names the identity
 * home the link must belong to.
 */
const postSetPassword = (
	path: "/check" | "" | "/renew",
	body: Record<string, string>
): Promise<Response> =>
	fetch(`${getConnectionTarget().serverUrl}/auth/set-password${path}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

const LinkCheckSchema = z.discriminatedUnion("live", [
	z.object({ live: z.literal(true), purpose: z.enum(["welcome", "reset"]) }),
	z.object({ live: z.literal(false) }),
]);

export type SetPasswordLinkState =
	| { state: "live"; purpose: "welcome" | "reset" }
	| { state: "dead" }
	| { state: "unavailable" };

/**
 * Is the link live? Uses nothing up. Anything but a well-formed answer is
 * "unavailable", never "dead": telling somebody their link is gone during an
 * outage would send them asking for a new one they do not need.
 */
export async function checkSetPasswordLink(
	token: string,
	address: string
): Promise<SetPasswordLinkState> {
	try {
		const response = await postSetPassword("/check", { token, address });
		if (!response.ok) return { state: "unavailable" };
		const parsed = LinkCheckSchema.safeParse(await response.json());
		if (!parsed.success) return { state: "unavailable" };
		return parsed.data.live
			? { state: "live", purpose: parsed.data.purpose }
			: { state: "dead" };
	} catch {
		return { state: "unavailable" };
	}
}

const PasswordSetSchema = z.object({ email: z.string() });
const PasswordRefusedSchema = z.object({
	error: z.enum(["dead-link", "weak-password"]),
});

export type SetPasswordResult =
	| { ok: true; email: string }
	| { ok: false; reason: "dead-link" | "weak-password" | "unavailable" };

export async function setPasswordWithLink(
	token: string,
	address: string,
	password: string
): Promise<SetPasswordResult> {
	try {
		const response = await postSetPassword("", { token, address, password });
		const data: unknown = await response.json();
		if (response.ok) {
			const set = PasswordSetSchema.safeParse(data);
			return set.success
				? { ok: true, email: set.data.email }
				: { ok: false, reason: "unavailable" };
		}
		const refused = PasswordRefusedSchema.safeParse(data);
		return {
			ok: false,
			reason: refused.success ? refused.data.error : "unavailable",
		};
	} catch {
		return { ok: false, reason: "unavailable" };
	}
}

/**
 * Ask for a fresh link to the address this one was sent to. The server answers
 * the same in every case, so there is nothing to read back, and a failure is
 * swallowed for the same reason.
 */
export async function renewSetPasswordLink(
	token: string,
	address: string
): Promise<void> {
	try {
		await (await postSetPassword("/renew", { token, address })).text();
	} catch {
		// Nothing the page could say differs from what it says anyway.
	}
}
